import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  TextArea,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type {
  NewsletterTemplate,
  NewsletterTemplateCreateRequest,
  NewsletterTemplateSummary,
  NewsletterTemplateUpdateRequest,
} from '../../../types/models/NewsletterTemplate';
import type { ApiResponseNewsletterTemplateSingle } from '../../../types/api/ApiResponseNewsletterTemplate';
import { formatBytes } from '../../../utils/formatBytes';

const NAME_MAX = 150;

// Backend field-error keys this form renders inline, on the matching input. Anything else in the
// `data` map (e.g. `body` for an empty change-set, or the template object on success) is surfaced
// in the form-level alert instead.
const FIELD_ERROR_KEYS = new Set(['name', 'twig']);

const byteLength = (value: string): number => new TextEncoder().encode(value).length;

interface NewsletterTemplateModalProps {
  /** Summary row to edit, or null to create a new template. */
  template: NewsletterTemplateSummary | null;
  onClose: () => void;
  /** Hand the created/updated full template back so the parent can upsert its row (no full refetch). */
  onSaved: (saved: NewsletterTemplate) => void;
}

/**
 * Create/edit modal for a newsletter template. A template is just a name plus a raw Twig body that
 * is authored externally (e.g. Claude Design) and pasted verbatim — so the body is a plain
 * monospace textarea, and the backend does the real validation (it tokenises + parses the Twig on
 * write and returns a `data.twig` error for a broken paste).
 *
 * The list row omits the (potentially large) `twig` body, so on EDIT this modal first fetches the
 * full template (`GET /admin/newsletters/templates/{id}`) to populate the textarea. The parent
 * mounts this component only while open, so every open starts from a fresh mount.
 */
function NewsletterTemplateModal({ template, onClose, onSaved }: NewsletterTemplateModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const isEdit = template !== null;

  const [name, setName] = useState(template?.name ?? '');
  const [twig, setTwig] = useState('');
  // Baselines for the PATCH change-set (an edit starts from the fetched body; a create from empty).
  const [originalName] = useState(template?.name ?? '');
  const [originalTwig, setOriginalTwig] = useState('');

  // Edit mode fetches the body before the form is usable.
  const [loadingBody, setLoadingBody] = useState(isEdit);
  const [bodyError, setBodyError] = useState<string | null>(null);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadBody = useCallback(async () => {
    if (template === null) {
      return;
    }
    setLoadingBody(true);
    setBodyError(null);
    try {
      const response = await apiFetch(`/admin/newsletters/templates/${template.id}`);
      const body = (await response.json().catch(() => null)) as ApiResponseNewsletterTemplateSingle | null;
      if (body?.status === 'success' && body.data?.newsletter_template) {
        const full = body.data.newsletter_template;
        setName(full.name);
        setTwig(full.twig);
        setOriginalTwig(full.twig);
      } else {
        setBodyError(body?.message ?? 'Could not load the template body. Try reopening the editor.');
      }
    } catch (err) {
      setBodyError(err instanceof Error ? err.message : 'Could not load the template body.');
    } finally {
      setLoadingBody(false);
    }
  }, [apiFetch, template]);

  // Fetch the body exactly ONCE per open. `loadBody` closes over `apiFetch`, whose identity flips
  // on every OIDC silent token renew — without this guard a renew mid-edit would re-run loadBody,
  // blank the form behind the spinner, and overwrite the user's unsaved name/twig with the server
  // values (silent data loss on an edit session longer than the token lifetime). The Retry button
  // calls loadBody() directly, which is safe: it's only reachable from the load-error state, where
  // the form isn't mounted and there is no unsaved input to clobber.
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (isEdit && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      void loadBody();
    }
  }, [isEdit, loadBody]);

  const clearFieldError = (key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const trimmedName = name.trim();
  // Compare `twig` raw (whitespace is significant in a template body); compare `name` trimmed.
  const nameChanged = trimmedName !== originalName.trim();
  const twigChanged = twig !== originalTwig;
  const hasChanges = nameChanged || twigChanged;

  const canSubmit =
    !isSaving &&
    !loadingBody &&
    bodyError === null &&
    trimmedName !== '' &&
    trimmedName.length <= NAME_MAX &&
    twig.trim() !== '' &&
    (isEdit ? hasChanges : true);

  const buildCreateBody = (): NewsletterTemplateCreateRequest => ({ name: trimmedName, twig });

  // PATCH is partial and an empty change-set is rejected — send only what actually changed.
  const buildUpdateBody = (): NewsletterTemplateUpdateRequest => {
    const patch: NewsletterTemplateUpdateRequest = {};
    if (nameChanged) {
      patch.name = trimmedName;
    }
    if (twigChanged) {
      patch.twig = twig;
    }
    return patch;
  };

  const splitFieldErrors = (
    body: ApiResponseNewsletterTemplateSingle | null,
  ): { errors: Record<string, string>; general: string | null } => {
    const errors: Record<string, string> = {};
    const generalParts: string[] = [];
    if (body?.data) {
      for (const [key, value] of Object.entries(body.data)) {
        if (typeof value !== 'string') {
          continue; // skips the newsletter_template object on the success shape
        }
        if (FIELD_ERROR_KEYS.has(key)) {
          errors[key] = value;
        } else {
          generalParts.push(value);
        }
      }
    }
    let general = generalParts.length > 0 ? generalParts.join(' ') : null;
    if (general === null && Object.keys(errors).length === 0) {
      general = body?.message ?? 'Could not save the template. Please try again.';
    }
    return { errors, general };
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const response = await apiFetch(
        isEdit ? `/admin/newsletters/templates/${template.id}` : '/admin/newsletters/templates',
        {
          method: isEdit ? 'PATCH' : 'POST',
          body: JSON.stringify(isEdit ? buildUpdateBody() : buildCreateBody()),
        },
      );
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions (including Twig syntax errors) to `status: 'error'` at HTTP 200.
      const body = (await response.json().catch(() => null)) as ApiResponseNewsletterTemplateSingle | null;

      if (body?.status === 'success' && body.data?.newsletter_template) {
        const saved = body.data.newsletter_template;
        onSaved(saved);
        addNotification({
          title: isEdit ? 'Template updated' : 'Template created',
          srTitle: 'Success alert',
          description: `${saved.name} was ${isEdit ? 'updated' : 'created'}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onClose();
        return;
      }

      const { errors, general } = splitFieldErrors(body);
      setFieldErrors(errors);
      setFormError(general);
      addNotification({
        title: isEdit ? 'Could not update template' : 'Could not create template',
        srTitle: 'Error alert',
        description: general ?? Object.values(errors)[0] ?? 'Please review the highlighted fields.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setFormError(message);
      addNotification({
        title: isEdit ? 'Could not update template' : 'Could not create template',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const nameError = fieldErrors['name'];
  const twigError = fieldErrors['twig'];

  return (
    <Modal variant="large" isOpen onClose={onClose} aria-label={isEdit ? 'Edit newsletter template' : 'Create newsletter template'}>
      <ModalHeader title={isEdit ? 'Edit template' : 'Create template'} />
      <ModalBody>
        {loadingBody ? (
          <Bullseye className="pf-v6-u-py-xl">
            <Spinner size="xl" aria-label="Loading template body" />
          </Bullseye>
        ) : bodyError !== null ? (
          <Alert
            variant="danger"
            isInline
            title="Could not load the template"
            actionLinks={
              <Button variant="link" isInline onClick={() => void loadBody()}>
                Retry
              </Button>
            }
          >
            {bodyError}
          </Alert>
        ) : (
          <Form
            id="newsletter-template-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (canSubmit) {
                void handleSubmit();
              }
            }}
          >
            {formError !== null && (
              <Alert variant="danger" isInline title={isEdit ? 'Update failed' : 'Creation failed'} className="pf-v6-u-mb-md">
                {formError}
              </Alert>
            )}

            <FormGroup label="Name" isRequired fieldId="newsletter-template-name">
              <TextInput
                id="newsletter-template-name"
                type="text"
                isRequired
                value={name}
                onChange={(_event, value) => {
                  setName(value);
                  clearFieldError('name');
                }}
                validated={nameError !== undefined || trimmedName.length > NAME_MAX ? 'error' : 'default'}
                isDisabled={isSaving}
              />
              <FormHelperText>
                <HelperText>
                  {nameError !== undefined ? (
                    <HelperTextItem variant="error">{nameError}</HelperTextItem>
                  ) : trimmedName.length > NAME_MAX ? (
                    <HelperTextItem variant="error">Name must be {NAME_MAX} characters or fewer.</HelperTextItem>
                  ) : (
                    <HelperTextItem>A short label for this template (1–{NAME_MAX} characters).</HelperTextItem>
                  )}
                </HelperText>
              </FormHelperText>
            </FormGroup>

            <FormGroup label="Twig body" isRequired fieldId="newsletter-template-twig">
              <TextArea
                id="newsletter-template-twig"
                aria-label="Twig template body"
                isRequired
                value={twig}
                onChange={(_event, value) => {
                  setTwig(value);
                  clearFieldError('twig');
                }}
                validated={twigError !== undefined ? 'error' : 'default'}
                isDisabled={isSaving}
                resizeOrientation="vertical"
                rows={18}
                style={{ fontFamily: 'var(--pf-t--global--font--family--mono, monospace)' }}
              />
              <FormHelperText>
                <HelperText>
                  {twigError !== undefined ? (
                    <HelperTextItem variant="error">{twigError}</HelperTextItem>
                  ) : (
                    <HelperTextItem>
                      Pasted verbatim and syntax-checked on save. {formatBytes(byteLength(twig))}
                    </HelperTextItem>
                  )}
                </HelperText>
              </FormHelperText>
            </FormGroup>
          </Form>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          type="submit"
          form="newsletter-template-form"
          isDisabled={!canSubmit}
          isLoading={isSaving}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default NewsletterTemplateModal;
